import React from 'react';
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, MapPin, Globe, Truck, FileWarning, Clock, PackageCheck } from 'lucide-react';
import type { OrderIssueStats } from '../../types/orderIssues';

interface IssueStatsCardsProps {
  stats: OrderIssueStats | null;
  loading: boolean;
}

const STAT_CARDS = [
  {
    key: 'total_open' as const,
    label: 'Open Issues',
    icon: AlertTriangle,
    gradient: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
  },
  {
    key: 'address_validation_error_count' as const,
    label: 'Address Errors',
    icon: MapPin,
    gradient: 'from-rose-500 to-red-600',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    text: 'text-rose-700 dark:text-rose-300',
  },
  {
    key: 'postnl_api_error_count' as const,
    label: 'PostNL Errors',
    icon: Truck,
    gradient: 'from-red-500 to-red-700',
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
  },
  {
    key: 'incomplete_address_count' as const,
    label: 'Incomplete Address',
    icon: FileWarning,
    gradient: 'from-orange-400 to-amber-600',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-700 dark:text-orange-300',
  },
  {
    key: 'postal_code_invalid_count' as const,
    label: 'Postal Code Invalid',
    icon: Globe,
    gradient: 'from-yellow-500 to-amber-600',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  {
    key: 'total_under_process' as const,
    label: 'Under Process',
    icon: Clock,
    gradient: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
  },
  {
    key: 'total_processed' as const,
    label: 'Processed',
    icon: PackageCheck,
    gradient: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    text: 'text-violet-700 dark:text-violet-300',
  },
  {
    key: 'total_resolved' as const,
    label: 'Resolved',
    icon: CheckCircle,
    gradient: 'from-emerald-500 to-green-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
];

export const IssueStatsCards: React.FC<IssueStatsCardsProps> = ({ stats, loading }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
      {STAT_CARDS.map((card) => {
        const Icon = card.icon;
        const value = stats ? Number(stats[card.key]) || 0 : 0;

        return (
          <div
            key={card.key}
            className={`${card.bg} rounded-xl p-4 border border-gray-100 dark:border-gray-700/50 transition-all hover:shadow-md`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${card.gradient} flex items-center justify-center flex-shrink-0`}
              >
                <Icon className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                {card.label}
              </span>
            </div>
            <div className={`text-2xl font-bold ${card.text}`}>
              {loading ? (
                <div className="h-8 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              ) : (
                value
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
